<div class="flex min-h-0 flex-1 flex-col gap-5 px-6 py-5">
  @if (summary(); as s) {
    <!-- Headline + controls -->
    <div class="flex items-end justify-between gap-5">
      <div>
        <div class="text-[12.5px] text-ink-2">Fleet adoption of {{ update().name }} {{ update().version }}</div>
        <div class="mt-1 flex items-baseline gap-2.5">
          <div class="ds-h1 font-num">{{ s.assignedCount }} / {{ s.fleetSize }}</div>
          <div class="text-xs text-ink-3">devices assigned · {{ s.statusCounts.ok }} confirmed on {{ update().version }}</div>
        </div>
      </div>

      <div class="flex items-center gap-2 text-[11.5px] text-ink-2">
        <div class="relative">
          <button
            type="button"
            (click)="toggleTenantMenu()"
            class="flex h-9 items-center gap-1.5 rounded-control bg-card px-3 shadow-hairline hover:bg-sunken"
          >
            {{ tenantFilterLabel() }}
            <app-icon [name]="tenantMenuOpen() ? 'icon-arrow-collapse' : 'icon-arrow-dropdown'" [size]="16" />
          </button>
          @if (tenantMenuOpen()) {
            <div class="absolute right-0 top-[calc(100%+6px)] z-20 max-h-64 w-56 overflow-y-auto rounded-control bg-card p-1 shadow-raised">
              <button
                type="button"
                (click)="selectTenantFilter(null)"
                class="block w-full rounded-[4px] px-2.5 py-2 text-left text-[13px] text-ink-1 hover:bg-sunken"
              >
                All tenants
              </button>
              @for (t of allTenants(); track t.id) {
                <button
                  type="button"
                  (click)="selectTenantFilter(t.id)"
                  class="block w-full rounded-[4px] px-2.5 py-2 text-left text-[13px] text-ink-1 hover:bg-sunken"
                >
                  {{ t.name }}
                </button>
              }
            </div>
          }
        </div>
        <button
          type="button"
          (click)="retryFailed()"
          [disabled]="s.statusCounts.failed === 0 || isRetrying()"
          class="flex h-9 items-center rounded-control bg-card px-3 shadow-hairline disabled:opacity-40 hover:bg-sunken"
        >
          {{ isRetrying() ? 'Retrying…' : 'Retry failed (' + s.statusCounts.failed + ')' }}
        </button>
      </div>
    </div>

    <!-- Status tiles — same KPI-tile convention as the admin tenants page
         (bg-card/shadow-hairline, font-num on the stat), so a big number
         reads the same wherever it appears in the admin. -->
    <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-5">
      <div class="rounded-card bg-card px-3.5 py-3 shadow-hairline">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Installed</div>
        <div class="mt-1 font-num text-[22px] font-semibold text-success">{{ s.statusCounts.ok }}</div>
      </div>
      <div class="rounded-card bg-card px-3.5 py-3 shadow-hairline">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Failed</div>
        <div class="mt-1 font-num text-[22px] font-semibold text-danger">{{ s.statusCounts.failed }}</div>
      </div>
      <div class="rounded-card bg-card px-3.5 py-3 shadow-hairline">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Pending sync</div>
        <div class="mt-1 font-num text-[22px] font-semibold text-ink-2">{{ pendingSync(s.statusCounts) }}</div>
      </div>
      <div class="rounded-card bg-card px-3.5 py-3 shadow-hairline">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Not assigned</div>
        <div class="mt-1 font-num text-[22px] font-semibold text-ink-3">{{ s.statusCounts.notAssigned }}</div>
      </div>
      <div class="rounded-card bg-card px-3.5 py-3 shadow-hairline">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Fleet on {{ update().version }}</div>
        <div class="mt-1 font-num text-[22px] font-semibold text-ink-1">{{ s.rolloutCoverage }}%</div>
      </div>
    </div>

    <!-- By tenant — a real table (app-grid), not a fixed bar-per-tenant
         list: at fleet scale (hundreds of tenants) that doesn't hold up.
         Sorted/paginated server-side; no search (see the class doc comment
         on why tenant name search isn't offered here). Own label + card
         border, tight together as one section, so it doesn't visually bleed
         into the device table below — app-grid has no boundary chrome of
         its own to separate stacked instances. -->
    <div class="flex flex-none flex-col gap-2">
      <div class="text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">By tenant</div>
      <div class="h-[420px] overflow-hidden rounded-card border border-hairline">
      <app-grid
        [columns]="tenantColumns"
        [data]="tenantRows()"
        [rowId]="tenantRowId"
        [loading]="isLoadingTenants()"
        [page]="tenantPage()"
        [pageSize]="tenantPageSize()"
        [totalElements]="tenantTotalElements()"
        [totalPages]="tenantTotalPages()"
        [sort]="tenantGridSort()"
        density="compact"
        (pageChange)="onTenantPageChange($event)"
        (pageSizeChange)="onTenantPageSizeChange($event)"
        (sortChange)="onTenantSortChange($event)"
      >
        <ng-template gridCell="tenantName" let-row>
          <div class="flex items-center gap-2.5">
            <span class="h-4 w-2 flex-none rounded-[2px]" [style.background]="tenantDot(asTenantRow(row).tenantId)"></span>
            <span class="truncate text-ink-1">{{ asTenantRow(row).tenantName }}</span>
          </div>
        </ng-template>
        <ng-template gridCell="progress" let-row>
          @let t = asTenantRow(row);
          @if (hasAnyAssignment(t)) {
            <div class="flex items-center gap-2.5">
              <div class="flex h-2 w-[130px] flex-none overflow-hidden rounded-[2px] bg-surface-2">
                <div class="h-full bg-success" [style.width.%]="barWidthPct(t.ok, t.fleetSize)"></div>
                <div
                  class="h-full bg-brand"
                  [style.width.%]="barWidthPct(pendingSync(t), t.fleetSize)"
                  [style.min-width.px]="pendingSync(t) > 0 ? 6 : 0"
                ></div>
                <div
                  class="h-full bg-danger"
                  [style.width.%]="barWidthPct(t.failed, t.fleetSize)"
                  [style.min-width.px]="t.failed > 0 ? 6 : 0"
                ></div>
              </div>
              <span class="text-[11px] text-ink-3">{{ progressPct(t) }}%</span>
            </div>
          } @else {
            <div class="h-2 w-[130px] rounded-[2px] bg-surface-2"></div>
          }
        </ng-template>
        <ng-template gridCell="installed" let-row>
          <span class="text-ink-2">{{ asTenantRow(row).ok }} / {{ asTenantRow(row).fleetSize }}</span>
        </ng-template>
        <ng-template gridCell="pending" let-row>
          <span class="text-ink-2">{{ pendingSync(asTenantRow(row)) || '—' }}</span>
        </ng-template>
        <ng-template gridCell="failed" let-row>
          @let failed = asTenantRow(row).failed;
          <span [class.text-danger]="failed > 0" [class.text-ink-2]="failed === 0">{{ failed || '—' }}</span>
        </ng-template>
      </app-grid>
      </div>
    </div>

    <!-- Device table — the shared app-grid, same component the Software
         Updates list uses. Status tabs double as the wireframe's filter
         pills; its built-in search box replaces the custom one. Same
         label + card treatment as the by-tenant table above, for a clear
         section break between the two stacked grids. -->
    <div class="flex min-h-0 flex-1 flex-col gap-2">
      <div class="text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">Devices</div>
      <div class="min-h-0 flex-1 overflow-hidden rounded-card border border-hairline">
      <app-grid
        [columns]="columns"
        [data]="devices()"
        [rowId]="rowId"
        [loading]="isLoadingDevices()"
        [page]="page()"
        [pageSize]="pageSize()"
        [totalElements]="totalElements()"
        [totalPages]="totalPages()"
        [tabs]="gridTabs()"
        [activeTabId]="statusFilter()"
        [searchable]="true"
        density="compact"
        (tabChange)="onTabChange($event)"
        (searchChange)="onSearchChange($event)"
        (pageChange)="onPageChange($event)"
        (pageSizeChange)="onPageSizeChange($event)"
        (sortChange)="onSortChange($event)"
      >
        <ng-template gridCell="clientId" let-row>
          <span class="truncate font-mono text-[11.5px] text-ink-1">{{ asDevice(row).clientId }}</span>
        </ng-template>
        <ng-template gridCell="tenant" let-row>
          <app-label-badge [dot]="tenantHueOf(asDevice(row).tenantId)">{{ asDevice(row).tenantName }}</app-label-badge>
        </ng-template>
        <ng-template gridCell="assignedAt" let-row>
          <span class="truncate text-sm text-ink-2">
            {{ asDevice(row).assignedAt ? (asDevice(row).assignedAt | date: 'MMM d') : '—' }}
          </span>
        </ng-template>
        <ng-template gridCell="updatedAt" let-row>
          <span class="truncate text-sm text-ink-2">{{ relativeTime(asDevice(row).updatedAt) }}</span>
        </ng-template>
      </app-grid>
      </div>
    </div>
  } @else if (isLoadingSummary()) {
    <!-- Summary skeleton mirroring the loaded layout (headline, KPI strip,
         the two table sections) so nothing jumps when the numbers land. The
         grids render their own bone rows once the summary gate opens. -->
    <div class="grid-skeleton flex min-h-0 flex-1 flex-col gap-5" aria-hidden="true">
      <div>
        <div class="h-3.5 w-64 animate-pulse rounded bg-surface-2"></div>
        <div class="mt-2 h-8 w-40 animate-pulse rounded bg-surface-2"></div>
      </div>
      <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-5">
        @for (bone of [0, 1, 2, 3, 4]; track bone) {
          <div class="rounded-card bg-card px-3.5 py-3 shadow-hairline">
            <div class="h-3 w-16 animate-pulse rounded bg-surface-2"></div>
            <div class="mt-2 h-6 w-12 animate-pulse rounded bg-surface-2"></div>
          </div>
        }
      </div>
      <div class="flex flex-none flex-col gap-2">
        <div class="h-2.5 w-16 animate-pulse rounded bg-surface-2"></div>
        <div class="h-[420px] animate-pulse rounded-card bg-surface-2"></div>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-2">
        <div class="h-2.5 w-14 animate-pulse rounded bg-surface-2"></div>
        <div class="min-h-32 flex-1 animate-pulse rounded-card bg-surface-2"></div>
      </div>
    </div>
  }
</div>
